<template>
    <div class="data-table">
        <div class="table-title" v-if="tableTitle || configColumn">
            <slot name="title-prepend"></slot>
            <span v-if="tableTitle && tableTitle !== ''">{{ tableTitle }}</span>
            <template v-if="configColumn">
                <v-btn @click="showDialogColumns" flat icon small color="primary">
                    <v-icon>mdi-cog</v-icon>
                </v-btn>
                <!--Dialog config field in table-->
                <v-dialog
                    no-click-animation
                    v-model="dialogColumns"
                    max-width="250"
                >
                    <div class="DialogColumns">
                        <!--Header dialog-->
                        <div class="dialog-header">
                            <div>{{ tableTitle || 'Configs' }}</div>
                            <v-btn small icon @click="dialogColumns = false">
                                <v-icon>mdi-close</v-icon>
                            </v-btn>
                        </div>
                        <!--Content dialog-->
                        <div class="fci-scrollbar" style="max-height: 70vh; overflow-y: auto">
                            <div
                                v-for="(column, idx) in headers"
                                :key="column.value"
                                :id="`option_${column.value}`"
                                class="item-config"
                            >
                                <v-checkbox
                                    :id="`${column.value}_${idx}`"
                                    v-model="columnsModel"
                                    color="#0052CC"
                                    :value="column.value"
                                    @change="toggleColumns(column.value)"
                                ></v-checkbox>
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <div
                                            class="text-content"
                                            v-bind="attrs"
                                            v-on="isTextTruncated($refs[`text_${column.value}`]) ? on : {}"
                                            :ref="`text_${column.value}`"
                                        >
                                            {{ column.text }}
                                        </div>
                                    </template>
                                    <div class="text-content">{{ column.text }}</div>
                                </v-tooltip>
                            </div>
                        </div>
                    </div>
                </v-dialog>
            </template>
            <slot name="title-append"></slot>
        </div>
        <template v-if="defaultHeader.length">
            <v-data-table
                v-model="selected"
                :select-all="selectable"
                :headers="defaultHeader"
                :items="getItems"
                :class="{'data-table-mobile': $utils.isMobile() && !keepDesktopLayout}"
                :hide-headers="$utils.isMobile() && !keepDesktopLayout"
                :hide-footer="true"
                :disable-initial-sort="true"
                hide-actions
            >
                <template v-slot:headers="props">
                    <tr>
                        <th v-if="selectable">
                            <div class="flexbox">
                                <v-checkbox
                                    :input-value="props.all"
                                    :indeterminate="props.indeterminate"
                                    color="primary"
                                    hide-details
                                    @click.stop="toggleAllCheckbox"
                                ></v-checkbox>
                            </div>
                        </th>
                        <th
                            v-for="header in props.headers"
                            :key="header.value"
                            :class="[
                                'column',
                                header.sortable ? 'sortable' : '',
                                sortDirection ? 'asc' : 'desc',
                                sortBy && header.value === sortBy ? 'active' : '',
                                header.center ? 'text-xs-center' : 'text-xs-left'
                            ]"
                            @click="updateSort(header.value, sortDirection, header.sortable)"
                        >
                            {{ header.text }}
                            <template v-if="header.sortable">
                                <v-icon small v-if="sortDirection === 'asc'">arrow_upward</v-icon>
                                <v-icon small v-if="sortDirection !== 'asc'">arrow_downward</v-icon>
                            </template>
                        </th>
                    </tr>
                </template>
                <template v-slot:items="props">
                    <tr v-if="$utils.isMobile() && !keepDesktopLayout"
                        class="mobile-row"
                        :class="rowClass"
                        @click="onRowClick(props.item)">
                        <td :colspan="defaultHeader.length" class="pa-0">
                            <div :class="header.center ? 'dtm-cell centerX': 'dtm-cell'">
                                <div class="dtm-row" v-for="header in defaultHeader" :key="header.value">
                                    <div class="dtm-header">{{ header.text }}</div>
                                    <div class="centerX">
                                        <template v-if="isTimeField(header)">
                                            {{ $filters.toHMS(props.item[header.value]) }}
                                        </template>
                                        <template v-else-if="header.value === 'status'">
                                            <div
                                                class="status-val"
                                                :style="{background: $utils.getStatusColor(props.item[header.value])}"
                                            >
                                                {{ props.item[header.value] }}
                                            </div>
                                        </template>
                                        <template v-else-if="header.type">
                                            <template v-if="header.type === 'custom'">
                                                <slot :name="header.id" :item="props.item"></slot>
                                            </template>
                                            <template v-if="header.type === 'datetime'">
                                                {{ $filters.formatDateTime(props.item[header.value]) }}
                                            </template>
                                        </template>
                                        <template v-else>
                                            {{
                                                typeof (header.value) === 'function' ? header.value(props.item) : props.item[header.value]
                                            }}
                                        </template>
                                    </div>
                                </div>
                            </div>
                        </td>
                    </tr>
                    <tr v-else @click="onRowClick(props.item)" :class="rowClass">
                        <td v-if="selectable">
                            <v-checkbox
                                v-model="props.selected"
                                @change="emitSelectionData()"
                                color="primary"
                                hide-details
                            ></v-checkbox>
                        </td>
                        <td v-for="header in defaultHeader"
                            :key="header.value"
                            :class="header.class ? header.class : ''"
                        >
                            <div :class="header.center ? 'centerX': ''">
                                <template
                                    v-if="isTimeField(header)">
                                    {{ $filters.toHMS(props.item[header.value]) }}
                                </template>
                                <template v-else-if="header.value === 'status'">
                                    <div
                                        class="status-val"
                                        :style="{background: $utils.getStatusColor(props.item[header.value])}"
                                    >
                                        {{ props.item[header.value] }}
                                    </div>
                                </template>
                                <template v-else-if="header.type">
                                    <template v-if="header.type === 'custom'">
                                        <slot :name="header.id" :item="props.item"></slot>
                                    </template>
                                    <template v-else-if="header.type === 'datetime'">
                                        {{ $filters.formatDateTime(props.item[header.value]) }}
                                    </template>
                                    <template v-else-if="header.type === 'date'">
                                        {{ formatDateCustom(props.item[header.value]) }}
                                    </template>
                                </template>
                                <template v-else>
                                    {{
                                        typeof (header.value) === 'function'
                                            ? header.value(props.item)
                                            : props.item[header.value]
                                    }}
                                </template>
                            </div>
                        </td>
                    </tr>
                </template>
                <template slot="no-data">
                    <div class="text-xs-center" style="font-size: 14px !important;">{{ $f.tc('noDataAvailable') }}</div>
                </template>
            </v-data-table>
        </template>
        <div v-if="!hidePager" class="pager-wrap">
            <div class="flexbox flex-xs-wrap">
                <div class="total-items">
                    <span>
                        {{ $f.tc('rows') }}:
                    </span>
                    <b>
                        <template v-if="totalItems === 0 || totalItems === '0'">
                            0 / 0
                        </template>
                        <template v-else-if="currentPerPage >= totalItems">
                            1 - {{ totalItems + ' / ' + totalItems }}
                        </template>
                        <template v-else>
                            {{ startRow + ' - ' + endRow + ' / ' + totalItems }}
                        </template>
                    </b>
                </div>
                <div class="item-per-page flexbox flex-justify-content-end" v-if="!disablePerPage">
                    <span class="per-page-label">
                        {{ $f.tc('rowsPerPage') }}
                    </span>
                    <div class="per-page-field">
                        <v-select
                            hide-details outlined
                            v-model="currentPerPage"
                            :items="currentPerPageArray"
                            @change="perPageChange"
                            class="input-custom"
                        ></v-select>
                    </div>
                </div>
                <div v-if="totalPage > 1" class="pager">
                    <v-pagination
                        v-model="pager"
                        :length="totalPage"
                        :value="currentPage"
                        :total-visible="$utils.isMobile() ? 4 : totalVisible"
                        @input="pageChange()"
                        @next="pageChange()"
                        @previous="pageChange()"
                    ></v-pagination>
                </div>
            </div>
        </div>
        <div v-if="showSelectedNav && selected.length" class="selection-actions elevation-24"
             :class="toggleNav ? 'nav-opened' : 'nav-closed'">
            <div class="flexbox flex-justify-content-center">
                <div class="remove-selection">
                    <v-btn small color="gray" dark outline @click="selected = []">
                        <span>selected: {{ selected.length }}</span>
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </div>
                <div class="selection-actions-slot">
                    <slot name="selection-actions"></slot>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import {mapState} from 'vuex'

export default {
    name: 'SimpleDataTable',
    props: {
        tableTitle: String,
        configColumn: Boolean,
        headers: Array,
        items: Array,
        rowClass: {
            type: String,
            default: ''
        },
        sortBy: String,
        sortDirection: String,
        totalItems: {
            type: Number,
            default: 0
        },
        totalPage: {
            type: Number,
            default: 1
        },
        currentPage: {
            type: Number,
            default: 1
        },
        disablePerPage: Boolean,
        itemsPerPage: Number,
        perPageArray: Array,
        totalVisible: {
            type: Number,
            default: 7
        },
        hidePager: Boolean,
        addIndex: Boolean,
        keepDesktopLayout: Boolean,
        selectable: Boolean,
        selectedProp: Array,
        showSelectedNav: {
            type: Boolean,
            default: true
        },
    },
    data() {
        return {
            pager: this.currentPage,
            currentPerPage: this.itemsPerPage || this.$const.itemPerPage,
            currentPerPageArray: this.perPageArray || this.$const.perPageArray,
            dialogColumns: false,
            columnsModel: [],
            selected: [],
        }
    },
    computed: {
        ...mapState({
            toggleNav: state => state.navigation
        }),
        defaultHeader() {
            let headersTMP = []
            if (this.addIndex) {
                headersTMP.push({
                    text: 'No.',
                    value: 'rowIndex',
                    sortable: false
                })
            }
            if (this.columnsModel) {
                for (let i = 0; i < this.headers.length; i++) {
                    let found = this.$lodash.find(this.columnsModel, value => value === this.headers[i].value)
                    if (found) {
                        headersTMP.push(this.headers[i])
                    }
                }
            } else {
                for (let i = 0; i < this.headers.length; i++) {
                    if (!this.headers[i]['invisible']) {
                        headersTMP.push(this.headers[i])
                    }
                }
            }

            return headersTMP
        },
        columnsModelTMP() {
            let columnsModelTMP = []
            if (this.addIndex) {
                columnsModelTMP.push('rowIndex')
            }
            for (let i = 0; i < this.headers.length; i++) {
                if (!this.headers[i]['invisible']) {
                    columnsModelTMP.push(this.headers[i].value)
                }
            }
            return columnsModelTMP
        },
        getItems() {
            if (this.addIndex && this.items.length) {
                this.items.map((item, idx) => {
                    this.$set(item, 'rowIndex', this.startRow + idx)
                })
            }
            return this.items
        },
        startRow() {
            return (this.currentPerPage * (this.currentPage - 1)) + 1
        },
        endRow() {
            return this.currentPerPage * this.currentPage >= this.totalItems ? this.totalItems : this.currentPerPage * this.currentPage
        }
    },
    methods: {
        onRowClick(rowData) {
            this.$emit('row-click', rowData)
        },
        pageChange() {
            this.$emit('page-change', this.pager)
        },
        perPageChange() {
            this.$emit('item-per-page-change', this.currentPerPage)
        },
        updateSort(key, direction, sortable) {
            if (!sortable) return
            this.$emit('sort-change', {
                value: key,
                direction: direction
            })
        },
        showDialogColumns() {
            this.dialogColumns = true
        },
        toggleColumns(column) {
            if (this.columnsModel.includes(column)) {
                this.headers.map(header => {
                    if (header.value === column) {
                        this.$set(header, 'invisible', false)
                    }
                })
            } else {
                this.headers.map(header => {
                    if (header.value === column) {
                        this.$set(header, 'invisible', true)
                    }
                })
            }
        },
        toggleAllCheckbox() {
            if (this.selected.length) {
                this.selected = []
            } else {
                this.selected = this.items.slice()
            }
            this.emitSelectionData()
        },
        emitSelectionData() {
            this.$nextTick().then(() => {
                this.$emit('selection-data', this.selected)
            })
        },
        isTimeField(header) {
            const timeFieldList = [
                'duration',
                'avg_call_time',
                'total_call_time',
                'call_transfer_duration',
                'bot_duration',
            ]
            return timeFieldList.includes(header.value)
        },
        formatDateCustom(date) {
            if (this.$filters.formatDate(date) === 'Invalid date') {
                return date
            } else {
                return this.$filters.formatDate(date)
            }
        },
        isTextTruncated(element) {
            if (!element) return false;
            if (Array.isArray(element)) {
                element = element[0];
            }
            return element ? element.offsetWidth < element.scrollWidth : false;
        }
    },
    watch: {
        currentPage() {
            this.pager = this.currentPage
        },
        selectedProp(value) {
            this.selected = [...value]
        },
        itemsPerPage(val) {
            this.currentPerPage = val
        },
        selected: {
            immediate: true,
            deep: true,
            handler: function (newVal) {
                this.$emit('updateSelected', newVal)
            }
        },
        headers: {
            immediate: true,
            handler: function (newHeaders, oldHeaders) {
                if (newHeaders && oldHeaders && oldHeaders.length === newHeaders.length) {
                    for (let i = 0; i < newHeaders.length; i++) {
                        newHeaders[i].invisible = oldHeaders[i].invisible
                    }
                }
                this.columnsModel = [...this.columnsModelTMP]
            }
        },
        dialogColumns: {
            immediate: true,
            handler: function (newValue, oldValue) {
                if (!newValue && oldValue && this.tableTitle === "Calling Info") {
                    localStorage.setItem(this.$route.params.campaignId, JSON.stringify(this.headers))
                }
            }
        }
    }
}
</script>
<style lang="scss" scoped>
.DialogColumns {
    background: #FFFFFF;
    border-radius: 5px !important;
    overflow: hidden;

    .item-config {
        display: flex;
        align-items: center;
        padding: 0 12px;
        height: 48px;
        border-bottom: 1px solid #E4EDFA;

        .text-content {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 180px;
            cursor: default; /* Default cursor when not truncated */
            font-size: 14px;

            &:hover {
                cursor: pointer; /* Pointer cursor only when truncated (tooltip will show) */
            }
        }
    }

    ::v-deep {
        .v-input--selection-controls {
            margin-top: 20px !important;
        }
    }
}

.data-table {
    .nowrap {
        white-space: nowrap;
    }
}

.per-page-label, .total-items {
    font-size: 14px;
}

.status-val {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 3px 10px;
    border-radius: 15px;
    color: #fff;
}

.input-custom {
    ::v-deep {
        .v-input__slot {
            height: 32px;
            background: #FFFFFF;
            border-radius: 5px;
            border: 1px solid #D9D9D9;
            outline: none;
            font-size: 14px;
            padding: 6px 12px;
            margin: 0;
            display: flex;
            align-items: center;

            &:before, &:after {
                border: none !important;
            }
        }
    }
}

::v-deep {
    table.v-table thead tr {
        height: 53px;
    }

    table.v-table tbody td {
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
    }
}
</style>
